/**
 * PartialCancelModal Component
 * Modal for cancelling specific items from a sale order
 * WCAG 2.1 AA compliant
 */

import React, { useState, useMemo } from 'react';
import { Modal } from '@/components/molecules';
import { Button, Icon } from '@/components/atoms';
import { useCancelSaleItemsMutation } from '@/hooks/useErpApi';
import { useAuth } from '@/hooks/useAuth';
import { CANCELLATION_REASONS, type CancellationReasonValue } from '@/schemas/cancellations';
import type { SaleResponse, SaleItemResponse } from '@kisanlink/erp-service';

type ModalStep = 'select' | 'processing' | 'success' | 'error';

interface SelectedItem {
  sale_item_id: string;
  quantity: number;
  maxQuantity: number;
  unitPrice: number;
  reason?: string;
}

export interface PartialCancelModalProps {
  sale: SaleResponse;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const PartialCancelModal: React.FC<PartialCancelModalProps> = ({
  sale,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<ModalStep>('select');
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedItem>>(new Map());
  const [overallReason, setOverallReason] = useState<CancellationReasonValue | ''>('');
  const [reasonDetails, setReasonDetails] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutateAsync: cancelItems } = useCancelSaleItemsMutation();
  const { user } = useAuth();

  // Calculate refund amount based on selected items
  const refundSummary = useMemo(() => {
    let totalQuantity = 0;
    let totalRefund = 0;

    selectedItems.forEach((item) => {
      totalQuantity += item.quantity;
      totalRefund += item.quantity * item.unitPrice;
    });

    return { totalQuantity, totalRefund, itemCount: selectedItems.size };
  }, [selectedItems]);

  const handleClose = () => {
    if (step === 'processing') return;
    setStep('select');
    setSelectedItems(new Map());
    setOverallReason('');
    setReasonDetails('');
    setErrorMessage(null);
    onClose();
  };

  const toggleItemSelection = (item: SaleItemResponse) => {
    const newSelectedItems = new Map(selectedItems);

    if (newSelectedItems.has(item.id)) {
      newSelectedItems.delete(item.id);
    } else {
      newSelectedItems.set(item.id, {
        sale_item_id: item.id,
        quantity: item.quantity,
        maxQuantity: item.quantity,
        unitPrice: item.selling_price,
      });
    }

    setSelectedItems(newSelectedItems);
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    const item = selectedItems.get(itemId);
    if (!item) return;

    const newQuantity = Math.max(1, Math.min(quantity, item.maxQuantity));
    const newSelectedItems = new Map(selectedItems);
    newSelectedItems.set(itemId, { ...item, quantity: newQuantity });
    setSelectedItems(newSelectedItems);
  };

  const updateItemReason = (itemId: string, reason: string) => {
    const item = selectedItems.get(itemId);
    if (!item) return;

    const newSelectedItems = new Map(selectedItems);
    newSelectedItems.set(itemId, { ...item, reason });
    setSelectedItems(newSelectedItems);
  };

  const handleSubmit = async () => {
    if (selectedItems.size === 0) return;

    // Validate required fields
    if (!overallReason) {
      setErrorMessage('Please provide a cancellation reason');
      return;
    }

    if (!user?.id) {
      setErrorMessage('User information not available. Please log in again.');
      return;
    }

    setStep('processing');
    setErrorMessage(null);

    try {
      // Validate and prepare items
      const itemsToCancel = Array.from(selectedItems.values())
        .filter(item => item.sale_item_id && item.sale_item_id.trim() !== '')
        .map((item) => ({
          sale_item_id: item.sale_item_id.trim(),
          quantity: item.quantity || 1,
          reason: item.reason?.trim() || undefined,
        }));

      if (itemsToCancel.length === 0) {
        setErrorMessage('No valid items selected for cancellation');
        setStep('select');
        return;
      }

      // Validate user ID
      if (!user?.id || user.id.trim() === '') {
        setErrorMessage('User information not available. Please log in again.');
        setStep('select');
        return;
      }

      // Validate reason
      if (!overallReason || overallReason.trim() === '') {
        setErrorMessage('Please provide a cancellation reason');
        setStep('select');
        return;
      }

      // Ensure all values are non-empty strings
      const trimmedReason = overallReason.trim();
      const trimmedReasonDetails = reasonDetails.trim();
      const trimmedPerformedBy = user.id.trim();

      if (!trimmedReason || trimmedReason === '') {
        setErrorMessage('Cancellation reason cannot be empty');
        setStep('select');
        return;
      }

      if (!trimmedPerformedBy || trimmedPerformedBy === '') {
        setErrorMessage('User ID cannot be empty');
        setStep('select');
        return;
      }

      // Ensure all items have valid sale_item_id
      const validItems = itemsToCancel.filter(item => item.sale_item_id && item.sale_item_id.trim() !== '');
      if (validItems.length === 0) {
        setErrorMessage('No valid items with sale_item_id found');
        setStep('select');
        return;
      }

      // Request payload in backend format (lowercase/snake_case)
      const requestPayload = {
        reason: trimmedReason,
        reason_details: trimmedReasonDetails || undefined,
        performed_by: trimmedPerformedBy,
        items: validItems,
      };

      // Debug logging
      if (import.meta.env.DEV) {
        console.log('[PartialCancelModal] Request payload:', {
          saleId: sale.id,
          payload: requestPayload,
        });
      }

      await cancelItems({
        id: sale.id,
        items: validItems,
        reason: trimmedReason,
        reason_details: trimmedReasonDetails || undefined,
        performed_by: trimmedPerformedBy,
      });

      setStep('success');
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1500);
    } catch (error) {
      setStep('error');
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to cancel items. Please try again.'
      );
    }
  };

  const handleRetry = () => {
    setStep('select');
    setErrorMessage(null);
  };

  const renderSelectStep = () => (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-info-50 border border-info-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-info-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-info-700">
              Select the items you want to cancel from this order. You can adjust the quantity to
              partially cancel an item.
            </p>
          </div>
        </div>
      </div>

      {/* Item Selection */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold text-neutral-900">Select Items to Cancel</h4>

        {sale.items && sale.items.length > 0 ? (
          sale.items.map((item) => {
            const isSelected = selectedItems.has(item.id);
            const selectedItem = selectedItems.get(item.id);

            return (
            <div
              key={item.id}
              className={`border rounded-lg p-4 transition-colors ${
                isSelected
                  ? 'border-error-300 bg-error-50'
                  : 'border-neutral-200 bg-white hover:border-neutral-300'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleItemSelection(item)}
                    className="w-5 h-5 rounded border-neutral-300 text-error-600 focus:ring-error-500"
                    aria-label={`Select item ${item.batch_id || item.id}`}
                  />
                </label>

                {/* Item Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-neutral-900 font-mono">
                        Batch: {item.batch_id?.substring(0, 12) || 'N/A'}
                      </p>
                      <p className="text-xs text-neutral-600 mt-1">
                        Original Qty: {item.quantity} | Unit Price: ₹{item.selling_price.toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-neutral-900">
                      ₹{item.line_total.toFixed(2)}
                    </p>
                  </div>

                  {/* Expanded Controls when selected */}
                  {isSelected && selectedItem && (
                    <div className="mt-4 pt-4 border-t border-error-200 space-y-3">
                      {/* Quantity Control */}
                      <div className="flex items-center gap-4">
                        <label className="text-sm text-neutral-700">Quantity to cancel:</label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateItemQuantity(item.id, selectedItem.quantity - 1)}
                            disabled={selectedItem.quantity <= 1}
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Decrease quantity"
                          >
                            <Icon name="Minus" size={16} />
                          </button>
                          <input
                            type="number"
                            value={selectedItem.quantity}
                            onChange={(e) =>
                              updateItemQuantity(item.id, parseInt(e.target.value) || 1)
                            }
                            min={1}
                            max={selectedItem.maxQuantity}
                            className="w-16 h-8 text-center border border-neutral-300 rounded-md focus:ring-2 focus:ring-error-500 focus:border-error-500"
                            aria-label="Cancellation quantity"
                          />
                          <button
                            type="button"
                            onClick={() => updateItemQuantity(item.id, selectedItem.quantity + 1)}
                            disabled={selectedItem.quantity >= selectedItem.maxQuantity}
                            className="w-8 h-8 flex items-center justify-center rounded-md bg-neutral-100 hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Increase quantity"
                          >
                            <Icon name="Plus" size={16} />
                          </button>
                          <span className="text-xs text-neutral-500">
                            of {selectedItem.maxQuantity}
                          </span>
                        </div>
                      </div>

                      {/* Per-item Reason (Optional) */}
                      <div>
                        <label className="text-sm text-neutral-700">
                          Reason for this item (optional):
                        </label>
                        <select
                          value={selectedItem.reason || ''}
                          onChange={(e) => updateItemReason(item.id, e.target.value)}
                          className="mt-1 w-full px-3 py-1.5 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-error-500 focus:border-error-500"
                        >
                          <option value="">No specific reason</option>
                          {CANCELLATION_REASONS.map((reason) => (
                            <option key={reason.value} value={reason.value}>
                              {reason.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Refund Preview */}
                      <div className="flex justify-between text-sm">
                        <span className="text-error-700">Refund for this item:</span>
                        <span className="font-semibold text-error-900">
                          ₹{(selectedItem.quantity * selectedItem.unitPrice).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-neutral-500">
            <p className="text-sm">No items available to cancel</p>
          </div>
        )}
      </div>

      {/* Overall Reason - Required */}
      {selectedItems.size > 0 && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="overall-reason"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Cancellation Reason <span className="text-error-600">*</span>
            </label>
            <select
              id="overall-reason"
              value={overallReason}
              onChange={(e) => setOverallReason(e.target.value as CancellationReasonValue)}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-error-500 focus:border-error-500"
            >
              <option value="">Select a reason</option>
              {CANCELLATION_REASONS.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
            {!overallReason && errorMessage && (
              <p className="text-error-600 text-sm mt-1">{errorMessage}</p>
            )}
          </div>

          {/* Reason Details - Optional */}
          <div>
            <label
              htmlFor="reason-details"
              className="block text-sm font-medium text-neutral-700 mb-2"
            >
              Reason Details (Optional)
            </label>
            <textarea
              id="reason-details"
              value={reasonDetails}
              onChange={(e) => setReasonDetails(e.target.value)}
              placeholder="Provide additional details about the cancellation..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-error-500 focus:border-error-500 resize-none"
            />
          </div>
        </div>
      )}

      {/* Refund Summary */}
      {selectedItems.size > 0 && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-warning-900 mb-3">Cancellation Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-warning-700">Items Selected:</span>
              <span className="ml-2 font-semibold text-warning-900">{refundSummary.itemCount}</span>
            </div>
            <div>
              <span className="text-warning-700">Total Quantity:</span>
              <span className="ml-2 font-semibold text-warning-900">
                {refundSummary.totalQuantity}
              </span>
            </div>
            <div>
              <span className="text-warning-700">Estimated Refund:</span>
              <span className="ml-2 font-bold text-warning-900">
                ₹{refundSummary.totalRefund.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-error-600 mx-auto mb-4" />
      <p className="text-neutral-600" aria-live="polite">
        Cancelling selected items and updating inventory...
      </p>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-12">
      <div className="bg-success-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <Icon name="Check" size={32} className="text-success-600" />
      </div>
      <h3 className="text-lg font-semibold text-success-900 mb-2">Items Cancelled</h3>
      <p className="text-neutral-600" aria-live="polite">
        {refundSummary.itemCount} item(s) cancelled. Inventory has been returned.
      </p>
    </div>
  );

  const renderErrorStep = () => (
    <div className="text-center py-8">
      <div className="bg-error-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <Icon name="X" size={32} className="text-error-600" />
      </div>
      <h3 className="text-lg font-semibold text-error-900 mb-2">Cancellation Failed</h3>
      <p className="text-neutral-600 mb-4" aria-live="assertive">
        {errorMessage || 'An error occurred while cancelling the items.'}
      </p>
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case 'select':
        return renderSelectStep();
      case 'processing':
        return renderProcessingStep();
      case 'success':
        return renderSuccessStep();
      case 'error':
        return renderErrorStep();
    }
  };

  const renderFooter = () => {
    switch (step) {
      case 'select':
        return (
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              variant="error" 
              onClick={handleSubmit} 
              disabled={selectedItems.size === 0 || !overallReason}
            >
              <Icon name="X" size={16} className="mr-2" />
              Cancel {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
            </Button>
          </div>
        );
      case 'processing':
        return null;
      case 'success':
        return null;
      case 'error':
        return (
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleClose}>
              Close
            </Button>
            <Button variant="primary" onClick={handleRetry}>
              Try Again
            </Button>
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'success' ? 'Items Cancelled' : 'Cancel Items'}
      size="lg"
      closeOnBackdropClick={step !== 'processing'}
      closeOnEscape={step !== 'processing'}
      footer={renderFooter()}
    >
      {renderContent()}
    </Modal>
  );
};
